import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  loginRequest,
  logoutRequest,
  sessionCheckRequest,
  socialProvidersRequest,
} from "../components/authApi";

const AuthContext = createContext();

const emptyProviders = {
  google: { enabled: false, label: "Continue with Google", url: "" },
  facebook: { enabled: false, label: "Continue with Facebook", url: "" },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socialProviders, setSocialProviders] = useState(emptyProviders);

  const refreshSession = async () => {
    try {
      const json = await sessionCheckRequest();
      if (json.status === "success") {
        setUser(json.data || null);
        if (json.social) {
          setSocialProviders({ ...emptyProviders, ...json.social });
        }
        return { ok: true, user: json.data };
      }

      setUser(null);
      return { ok: false, message: json.message || "No active session" };
    } catch (error) {
      console.error("Session check error:", error);
      setUser(null);
      return { ok: false, message: "Unable to verify session." };
    }
  };

  const loadSocialProviders = async () => {
    try {
      const json = await socialProvidersRequest();
      if (json.status === "success" && json.providers) {
        setSocialProviders({ ...emptyProviders, ...json.providers });
      }
    } catch (error) {
      console.warn("Unable to load social providers:", error);
    }
  };

  const login = async (loginId, password) => {
    try {
      const json = await loginRequest(loginId, password);
      if (json.status === "success") {
        setUser(json.data || null);
        return { ok: true, data: json.data };
      }

      return { ok: false, message: json.message || "Login failed" };
    } catch (error) {
      console.error("Login error:", error);
      return { ok: false, message: "Network or server error" };
    }
  };

  const logout = async () => {
    try {
      await logoutRequest();
    } catch (error) {
      console.error("Logout network error:", error);
    } finally {
      setUser(null);
    }
  };

  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      setLoading(true);
      await Promise.all([refreshSession(), loadSocialProviders()]);
      if (mounted) {
        setLoading(false);
      }
    };

    bootstrap();
    return () => {
      mounted = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      socialProviders,
      login,
      logout,
      refreshSession,
      updateUser: setUser,
    }),
    [user, loading, socialProviders]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be within AuthProvider");
  return ctx;
}
